import { refDebounced, useConfirmDialog, useDebounceFn } from '@vueuse/core'
import { useRouteParams } from '@vueuse/router'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { AttemptStatus, GradingStatus } from '~/types/TestAttempt'
import type {
  TestSubmissionSummary,
  TestSubmissionsPaginationMeta,
} from '~/types/Submission'
import {
  SseMessageType,
  type FileSubmissionBatchUpdateMessage,
  type TestAttemptGradeStatusUpdateMessage,
  type TestAttemptStatusUpdateMessage,
  type TestGradeRankingInvalidationUpdateMessage,
  type TestGradeRankingUpdateMessage,
} from '~/types/SseMessage'
import { useTestSubmissionGroupsStore } from '@/stores/testSubmissionGroups'
import { useTestSubmissionOverviewStore } from '@/stores/testSubmissionOverview'
import { useUserStore } from '@/stores/user'
import { useWorkspaceStore } from '@/stores/workspace'
import { SubmissionGroupStatus } from '~/types/SubmissionGroup'
import type { TestGradeRanking } from '~/types/GradeRanking'
import { TestService } from '~/services/api/test/test'
import { TestSubmissionsService } from '~/services/api/test/test-submissions'
import { TestSubmissionGradingService } from '~/services/api/test/test-submission-grading'
import { TestSubmissionGroupsService } from '~/services/api/test/test-submission-groups'
import { TestGoogleDriveSubmissionsService } from '~/services/api/test/test-google-drive-submissions'
import type { TestAttemptBatchSelectionParameters } from '~/types/TestAttemptBatchSelectionParameters'

export const useTestSubmissionsStore = defineStore('testSubmissions', () => {
  // SERVICES ==========================================
  const testService = new TestService()
  const testSubmissionsService = new TestSubmissionsService()
  const testSubmissionGradingService = new TestSubmissionGradingService()
  const testSubmissionGroupsService = new TestSubmissionGroupsService()
  const ltiGradeSyncService = new LtiGradeSyncService()
  const googleDriveSubmissionsService = new TestGoogleDriveSubmissionsService()
  const { addToast, removeToastsByType } = useToastNotifications()
  const { showDialog } = useDialogService()
  const { authenticate } = useGoogleAuth()
  const { openPicker } = useGooglePicker()
  const testSubmissionGroupsStore = useTestSubmissionGroupsStore()
  const workspaceStore = useWorkspaceStore()
  const testSubmissionOverviewStore = useTestSubmissionOverviewStore()
  const userStore = useUserStore()

  // SSE =========================================
  async function updateTestSubmissionSummaryByGradeId(
    testAttemptGradeId: string,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      captureError(
        new Error(
          `Attempted to update test attempt grade ${testAttemptGradeId} but no test ID found`,
        ),
      )
      return
    }

    const testAttemptId =
      submissionsPaginated.value.find(
        (s) => s.testAttemptGrade.id === testAttemptGradeId,
      )?.id ?? undefined

    if (!testAttemptId) {
      return
    }

    const { data, error } =
      await testSubmissionsService.getTestSubmissionSummary(
        testId.value,
        testAttemptId,
      )

    if (error !== null) {
      console.error('Error updating test attempt grade:', error)
      return
    }

    const submissionIndex = submissionsPaginated.value.findIndex(
      (testAttempt) => testAttempt.id === data.id,
    )

    if (submissionIndex === -1) {
      return
    }

    submissionsPaginated.value[submissionIndex] = data
  }

  async function handleTestAttemptGradeStatusUpdate(
    data: TestAttemptGradeStatusUpdateMessage,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    if (!data.data) {
      captureError(
        new Error(
          `Received test attempt grade update SSE message but no data was provided.`,
        ),
      )
      return
    }

    if (data.data.testId !== testId.value) return

    if (!data.data.status) {
      captureError(
        new Error(
          `Received test attempt grade update SSE message but no status was provided.`,
        ),
      )
      return
    }

    // Grade ingest sets the submission flags, which moves the filter counts
    // even when the graded row isn't on this page.
    if (
      data.data.status === GradingStatus.Complete ||
      data.data.status === GradingStatus.Error
    ) {
      updateListAfterGradingDebounced()
    }

    const submissionIndex = submissionsPaginated.value.findIndex(
      (s) => s.testAttemptGrade.id === data.id,
    )

    if (submissionIndex === -1) {
      return
    }

    // if grade status is complete or error, fetch the full summary to get all fields (e.g. error message for tooltip)
    if (
      data.data.status === GradingStatus.Complete ||
      data.data.status === GradingStatus.Error
    ) {
      await updateTestSubmissionSummaryByGradeId(data.id)
      return
    }

    // otherwise, update the grade status exclusively
    submissionsPaginated.value[submissionIndex].testAttemptGrade.status =
      data.data.status
  }

  function handleTestGradeRankingInvalidationUpdate(
    data: TestGradeRankingInvalidationUpdateMessage,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    if (data.data?.testId !== testId.value) {
      return
    }

    if (data.data?.isValid === false) {
      if (!testGradeRanking.value) return

      testGradeRanking.value.isValid = false
    }
  }

  async function handleTestGradeRankingUpdate(
    data: TestGradeRankingUpdateMessage,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    if (data.data?.testId !== testId.value) {
      return
    }

    if (!testGradeRanking.value) return

    if (!data.data?.testId) {
      captureError(
        new Error(
          `Received test grade ranking update SSE message but no test ID was provided.`,
        ),
      )
    }

    const { data: refreshedData, error } =
      await testSubmissionsService.getTestGradeRanking(data.data.testId)

    if (error !== null) {
      captureError(error)
      addToast({
        type: 'error',
        heading: 'Error fetching test grade ranking',
        message: 'There was an error fetching the test grade ranking.',
        life: 5000,
      })
      return
    }

    testGradeRanking.value = refreshedData
  }

  const uploadBatches = ref<
    Record<
      string,
      {
        uploadedCount: number
        totalCount: number
        canClose: boolean
        skippedFiles?: number
        failedCount?: number
        batchDone?: boolean
      }
    >
  >({})

  // Keyed by batchId. Lives outside the reactive ref so timers don't trigger
  // unnecessary re-renders.
  const uploadBatchFallbackTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >()
  const uploadBatchSseProgressState = new Map<
    string,
    { offset: number; lastChunkUploadedCount: number }
  >()
  const uploadBatchSseSeenAttemptIds = new Map<string, Set<string>>()

  // Schedule (or reschedule) a 2-minute inactivity fallback for a batch.
  // Each call resets the clock, so the toast only auto-closes after 2 minutes
  // with no SSE activity — not 2 minutes from when the upload HTTP call returned.
  function scheduleUploadFallbackClear(batchId: string) {
    const existing = uploadBatchFallbackTimers.get(batchId)
    if (existing) clearTimeout(existing)

    const timer = setTimeout(() => {
      uploadBatchFallbackTimers.delete(batchId)
      if (uploadBatches.value[batchId]) {
        clearUploadProgressToast(batchId)
      }
    }, 120_000)

    uploadBatchFallbackTimers.set(batchId, timer)
  }

  function clearUploadProgressToast(batchId: string) {
    const timer = uploadBatchFallbackTimers.get(batchId)
    if (timer) {
      clearTimeout(timer)
      uploadBatchFallbackTimers.delete(batchId)
    }
    uploadBatchSseProgressState.delete(batchId)
    uploadBatchSseSeenAttemptIds.delete(batchId)
    delete uploadBatches.value[batchId]
  }

  async function handleTestAttemptStatusUpdate(
    data: TestAttemptStatusUpdateMessage,
  ) {
    if (!testId.value || typeof testId.value !== 'string') return

    if (testId.value !== data.data?.testId) return

    if (!data.data?.status) {
      captureError(
        new Error(
          'Received test attempt status update SSE message but no status was provided.',
        ),
      )
      return
    }

    const submissionIndex = submissionsPaginated.value.findIndex(
      (s) => s.id === data.id,
    )

    if (submissionIndex !== -1) {
      if (data.data.status === AttemptStatus.Complete) {
        const { data: refreshedSubmission, error } =
          await testSubmissionsService.getTestSubmissionSummary(
            testId.value,
            submissionsPaginated.value[submissionIndex].id,
          )

        if (error === null) {
          submissionsPaginated.value[submissionIndex] = refreshedSubmission
        }
      } else {
        submissionsPaginated.value[submissionIndex].status = data.data.status
      }
    }

    if (
      typeof data.data.uploadedCount !== 'number' ||
      typeof data.data.totalCount !== 'number'
    )
      return

    const batchId = data.data.batchId

    if (!batchId) return

    if (!uploadBatches.value[batchId]) {
      // Batch not found — either page was refreshed mid-upload or batchId is missing.
      // Re-hydrate the batch from the SSE event so the toast reappears.
      uploadBatches.value[batchId] = {
        uploadedCount: data.data.uploadedCount,
        totalCount: data.data.totalCount,
        canClose: false,
      }
      uploadBatchSseProgressState.set(batchId, {
        offset: 0,
        lastChunkUploadedCount: data.data.uploadedCount,
      })
      const rehydratedSeenIds = new Set<string>()
      if (data.id) {
        rehydratedSeenIds.add(data.id)
      }
      uploadBatchSseSeenAttemptIds.set(batchId, rehydratedSeenIds)

      addToast({
        type: 'file-submission-upload',
        heading: 'Uploading',
        message: 'Uploading submission(s) to the server.',
        closeable: false,
        data: { batchId },
      })

      // Start the fallback timer immediately so rehydrated toasts are still
      // cleaned up if no further SSE messages arrive (e.g. network drop or
      // upload already finished before the page reloaded).
      scheduleUploadFallbackClear(batchId)
      return
    }

    const previousDisplayedCount = uploadBatches.value[batchId].uploadedCount
    const sseState = uploadBatchSseProgressState.get(batchId) ?? {
      offset: 0,
      lastChunkUploadedCount: 0,
    }
    const seenAttemptIds =
      uploadBatchSseSeenAttemptIds.get(batchId) ?? new Set<string>()

    // SSE uploadedCount is chunk-local (1..chunkSize). When it drops, a new chunk
    // started; carry the previous displayed count as the new global offset.
    if (data.data.uploadedCount < sseState.lastChunkUploadedCount) {
      sseState.offset = previousDisplayedCount
    }

    const normalisedUploadedCount = Math.min(
      sseState.offset + data.data.uploadedCount,
      data.data.totalCount,
    )

    if (data.id) {
      seenAttemptIds.add(data.id)
    }
    uploadBatchSseSeenAttemptIds.set(batchId, seenAttemptIds)
    const attemptBasedUploadedCount = Math.min(
      seenAttemptIds.size,
      data.data.totalCount,
    )

    sseState.lastChunkUploadedCount = data.data.uploadedCount
    uploadBatchSseProgressState.set(batchId, sseState)

    uploadBatches.value[batchId].uploadedCount = Math.max(
      previousDisplayedCount,
      normalisedUploadedCount,
      attemptBasedUploadedCount,
    )
    uploadBatches.value[batchId].totalCount = Math.max(
      uploadBatches.value[batchId].totalCount,
      data.data.totalCount,
    )

    // SSE is alive — push the inactivity deadline forward.
    scheduleUploadFallbackClear(batchId)
  }

  // META ==============================================
  const testId = useRouteParams<string>('testId')

  // SUBMISSIONS =========================================
  // Search / filters are plain refs (the project-wide list pattern), cleared
  // per test in reset(). Comma-separated group ids / flag keys OR within each;
  // grade bounds 0–100, graded-only.
  const searchQuery = ref('')

  const fileSubmissionStatus = ref('')

  const gradingStatus = ref('')

  const debouncedSearchQuery = refDebounced(searchQuery, 500)

  const sortField = ref('score')

  const sortOrder = ref<'asc' | 'desc'>('asc')

  const groupIds = ref('')

  const flags = ref('')

  const gradeMin = ref('')

  const gradeMax = ref('')

  // Single source of truth for the active list filters, shared by the paginated
  // list query AND every "select all matching this view" batch payload, so a
  // filter can never reach the list but miss a batch action (the destructive
  // select-all gotcha).
  // Guard against a hand-edited ?gradeMin=abc reaching the query as NaN.
  function toGradeBound(value: string): number | undefined {
    if (!value) return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const activeFilters = computed(() => ({
    query: debouncedSearchQuery.value || undefined,
    fileSubmissionStatus: fileSubmissionStatus.value || undefined,
    gradingStatus: gradingStatus.value || undefined,
    groupIds: groupIds.value || undefined,
    flags: flags.value || undefined,
    gradeMin: toGradeBound(gradeMin.value),
    gradeMax: toGradeBound(gradeMax.value),
  }))

  function withActiveFilters<T extends object>(selection: T) {
    return { ...selection, ...activeFilters.value }
  }

  const usePaginationParams = computed(() => ({
    sort: sortField.value,
    order: sortOrder.value,
    ...activeFilters.value,
  }))

  const {
    data: submissionsPaginated,
    isLoading,
    meta: submissionsMeta,
    page,
    limit: rowsPerPage,
    error: paginationError,
    status: submissionsStatus,
    refresh: refreshSubmissionsPaginated,
  } = usePagination<TestSubmissionSummary, TestSubmissionsPaginationMeta>(
    computed(() =>
      testId.value
        ? `/tests/${testId.value}/attempt-summaries/paginated`
        : undefined,
    ),
    {
      initialPage: 1,
      initialLimit: 20,
      params: usePaginationParams,
    },
  )

  // COUNTS =========================================
  // The page's one source of counts: the newest paginated meta, from the list's
  // own fetch or the background refetch after grades land (GRADE UPDATES).
  // Read counts from here, never from submissionsMeta.
  const listCounts = shallowRef<TestSubmissionsPaginationMeta | null>(null)

  watch(
    submissionsMeta,
    (meta) => {
      listCounts.value = meta ?? null
    },
    { immediate: true },
  )

  const totalItems = computed(() => listCounts.value?.totalItems ?? 0)

  // Flag counts: fixed (whole-test, ignores filters) drives the dropdown option
  // counts; faceted (recomputed with filters) drives the Flags column-header
  // badge. unfilteredTotalItems is the whole-test total for "Showing X of Y"
  // and the bulk-bar "other N not changed" line.
  const flagCounts = computed(
    () => listCounts.value?.extraMeta?.flagCounts ?? {},
  )
  const flaggedCount = computed(
    () => listCounts.value?.extraMeta?.flaggedCount ?? 0,
  )
  const unfilteredTotalItems = computed(
    () => listCounts.value?.extraMeta?.unfilteredTotalItems ?? totalItems.value,
  )

  // GRADE UPDATES =========================================
  // After grades land, refetch this page in the background and take its counts.
  // The rows on screen are left alone, so the list never reorders; the graded
  // row itself is updated in place above. A service call rather than useFetch's refresh(): no
  // loading overlay, and a failed request leaves the table as it was.
  let listUpdateRequestId = 0

  async function updateListAfterGrading() {
    if (!testId.value || typeof testId.value !== 'string') return
    const requestId = ++listUpdateRequestId

    const { data, error } =
      await testSubmissionsService.getTestSubmissionSummariesPaginated(
        testId.value,
        {
          page: page.value,
          limit: rowsPerPage.value,
          ...usePaginationParams.value,
        },
      )
    // A newer update, or a fetch of the list's own, has superseded this one.
    if (error !== null || requestId !== listUpdateRequestId) return

    listCounts.value = data.meta
  }

  const updateListAfterGradingDebounced = useDebounceFn(
    updateListAfterGrading,
    2000,
    { maxWait: 5000 },
  )

  // The list's own fetch (page, sort, filters, $refresh) supersedes any update
  // in flight.
  watch(submissionsStatus, (status) => {
    if (status === 'pending') listUpdateRequestId++
  })

  // TABLE SELECTION =========================================
  const {
    state: selectionState,
    toggleSelectedId,
    selectRange,
    selectAll,
    deselectAll,
    selectedCount,
  } = usePaginatedSelectionState(totalItems)

  // Reset to page 1 and clear the selection whenever any filter or the search
  // query changes. Filtering while on a later page otherwise shows an empty
  // page, because usePagination never resets page on a params change.
  watch(
    [
      debouncedSearchQuery,
      fileSubmissionStatus,
      gradingStatus,
      groupIds,
      flags,
      gradeMin,
      gradeMax,
    ],
    () => {
      page.value = 1
      deselectAll()
    },
  )

  // GRADE RANKING =========================================
  const testGradeRanking = ref<TestGradeRanking | null>(null)

  async function populateTestGradeRanking(testId: string) {
    const { data, error } =
      await testSubmissionsService.getTestGradeRanking(testId)

    if (error !== null) {
      captureError(error)
      addToast({
        type: 'error',
        heading: 'Error fetching test grade ranking',
        message: 'There was an error fetching the test grade ranking.',
        life: 5000,
      })
      return
    }

    testGradeRanking.value = data
  }

  async function forceRefreshTestGradeRanking() {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const { data, error } =
      await testSubmissionsService.refreshTestGradeRanking(testId.value)

    if (error !== null) {
      captureError(error)
      addToast({
        type: 'error',
        heading: 'Error fetching test grade ranking',
        message: 'There was an error refreshing the test grade ranking.',
        life: 5000,
      })
      return
    }

    testGradeRanking.value = data
  }

  addToWindowDevTools(
    'forceRefreshTestGradeRanking',
    forceRefreshTestGradeRanking,
  )

  // TEST LOCKING =========================================
  async function checkTestLockForGrading() {
    await testSubmissionOverviewStore.$ensureInitialised()

    if (!testSubmissionOverviewStore.test) return false

    if (testSubmissionOverviewStore.test.lockedAt) return true

    const shouldLock = await showDialog({
      type: 'confirm',
      props: {
        heading: 'Lock & Grade',
        message:
          'The test needs to be locked before grading can begin. Do you want to lock the test and continue?',
        confirmButtonLabel: 'Lock & Grade',
        cancelButtonLabel: 'Cancel',
      },
    })

    if (!shouldLock) return false

    if (!userStore.user?.id) return false

    const { error } = await testService.lockTest(
      testId.value,
      userStore.user.id,
    )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error locking test',
        message:
          'There was an error locking the test and grading was aborted. Please try again.',
        life: 5000,
      })

      return false
    }

    testSubmissionOverviewStore.$refresh()

    return true
  }

  // REQUEST GRADES =========================================
  async function requestTestAttemptGrade(testAttemptId: string) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const testAttempt = submissionsPaginated.value.find(
      (submission) => submission.id === testAttemptId,
    )

    if (!testAttempt) {
      return
    }

    if (testAttempt.status !== AttemptStatus.Complete) {
      addToast({
        type: 'error',
        heading: 'Error requesting grade',
        message: 'Test attempt must be complete to request a grade.',
        life: 5000,
      })
      return
    }

    const isLocked = await checkTestLockForGrading()

    if (!isLocked) return

    const { error } = await testService.requestTestAttemptGrade(
      testId.value,
      testAttemptId,
    )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error requesting grade',
        message: 'There was an issue requesting the grade.',
        life: 5000,
      })
      return
    }

    await $refresh()

    addToast({
      heading: 'Grade requested',
      message: 'Grade requested successfully.',
      life: 5000,
    })
  }

  async function requestTestAttemptReGrade(testAttemptId: string) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const testAttempt = submissionsPaginated.value.find(
      (submission) => submission.id === testAttemptId,
    )

    if (!testAttempt) {
      return
    }

    if (testAttempt.status !== AttemptStatus.Complete) {
      addToast({
        type: 'error',
        heading: 'Error requesting grade',
        message: 'Test attempt must be complete to request a grade.',
        life: 5000,
      })
      return
    }

    const shouldContinue = await showDialog({
      type: 'confirm',
      props: {
        heading: 'Request regrade',
        message: testAttempt.testAttemptGrade?.isEdited
          ? 'Are you sure you want to request a regrade? This will overwrite the current grade, including changes made by a lecturer. This cannot be reversed.'
          : 'Are you sure you want to request a regrade? This will overwrite the current grade. This cannot be reversed.',
        confirmButtonLabel: 'Request regrade',
      },
    })

    if (!shouldContinue) return

    const isLocked = await checkTestLockForGrading()

    if (!isLocked) return

    const { error } = await testService.requestTestAttemptReGrade(
      testId.value,
      testAttemptId,
    )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error requesting grade',
        message: 'There was an issue requesting the grade.',
        life: 5000,
      })
      return
    }

    await $refresh()

    addToast({
      heading: 'Grade requested',
      message: 'Grade requested successfully.',
      life: 5000,
    })
  }

  async function retryFileSubmissionConversion(testAttemptId: string) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const shouldContinue = await showDialog({
      type: 'confirm',
      props: {
        heading: 'Retry File Conversion',
        message:
          'Are you sure you want to retry the file conversion? This will re-process the submission file.',
        confirmButtonLabel: 'Retry',
      },
    })

    if (!shouldContinue) return

    const { error } = await testService.retryFileSubmissionConversion(
      testId.value,
      testAttemptId,
    )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error retrying file conversion',
        message: 'There was an issue retrying the file conversion.',
        life: 5000,
      })
      return
    }

    await $refresh()

    addToast({
      heading: 'File conversion retried',
      message: 'File conversion has been queued for retry.',
      life: 5000,
    })
  }

  async function countEligibleSubmissionsForReprocessing(
    selection: TestAttemptBatchSelectionParameters,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return { data: null, error: null }
    }

    return await testService.countEligibleFileSubmissionsForRetry(
      testId.value,
      withActiveFilters(selection),
    )
  }

  async function batchRetryFileSubmissionConversion(
    selection: TestAttemptBatchSelectionParameters,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const selectionWithFilters = withActiveFilters(selection)

    const { data: preview, error: previewError } =
      await countEligibleSubmissionsForReprocessing(selection)

    if (previewError !== null) {
      addToast({
        type: 'error',
        heading: 'Error checking eligible submissions',
        message:
          'There was an issue checking which submissions can be retried.',
        life: 5000,
      })
      return
    }

    if (!preview || preview.eligibleCount === 0) {
      addToast({
        heading: 'No submissions to re-process',
        message:
          'None of the selected submissions have an uploaded file to re-process.',
        life: 5000,
      })
      return
    }

    const shouldContinue = await showDialog({
      type: 'confirm',
      props: {
        heading: 'Re-process Submissions',
        message: `Are you sure you want to retry file conversion for ${preview.eligibleCount} ${
          preview.eligibleCount === 1 ? 'submission' : 'submissions'
        }? This will re-process the submission files.`,
        confirmButtonLabel: 'Retry',
      },
    })

    if (!shouldContinue) return

    const { data, error } =
      await testService.batchRetryFileSubmissionConversion(
        testId.value,
        selectionWithFilters,
      )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error re-processing submissions',
        message: 'There was an issue re-processing the submissions.',
        life: 5000,
      })
      return
    }

    await $refresh()

    const skippedNote =
      data && data.skippedCount > 0
        ? ` ${data.skippedCount} submission${data.skippedCount === 1 ? ' was' : 's were'} skipped (no uploaded file to re-process).`
        : ''

    addToast({
      heading: 'Submissions queued for re-processing',
      message: `The eligible submissions have been queued for re-processing.${skippedNote}`,
      life: 5000,
    })
  }

  const isRequestingGrades = ref(false)

  interface RegradePromptChoice {
    regrade: boolean
    excludeEdited: boolean
  }

  const regradePromptDetails = ref<{
    allGraded: boolean
    editedCount: number
  } | null>(null)

  const {
    isRevealed: isRegradePromptOpen,
    reveal: revealRegradePrompt,
    confirm: answerRegradePrompt,
    cancel: dismissRegradePrompt,
  } = useConfirmDialog<undefined, RegradePromptChoice>()

  async function askRegradeWithEdits(allGraded: boolean, editedCount: number) {
    regradePromptDetails.value = { allGraded, editedCount }
    try {
      const { data, isCanceled } = await revealRegradePrompt()
      return isCanceled || !data ? null : data
    } finally {
      regradePromptDetails.value = null
    }
  }

  async function batchRequestTestAttemptGrades(
    request: Omit<GradeMultipleRequest, 'regrade'>,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    // 1. CHECK TEST LOCK
    const isLocked = await checkTestLockForGrading()

    if (!isLocked) return

    // Disable the action and show a spinner for the whole flow (eligibility
    // check + request). The finally block guarantees both are cleaned up on
    // every exit path; the spinner is only suspended while the regrade dialog
    // is open.
    isRequestingGrades.value = true
    addToast({
      type: 'spinner',
      heading: 'Requesting grades',
      message: 'Requesting grades...',
    })

    try {
      // 2. CHECK ELIGIBILITY OF SELECTED SUBMISSIONS
      // Use the selection state to get the eligiblility of the selected submissions
      const { data: eligibilityData, error: eligibilityError } =
        await testSubmissionGradingService.getGradingEligibility(
          testId.value,
          withActiveFilters(request),
        )

      if (eligibilityError !== null) {
        addToast({
          type: 'error',
          heading: 'Error requesting grades',
          message: 'There was an issue requesting the grades.',
          life: 5000,
        })
        return
      }

      // 2.1 If all are not eligible, show an error and return
      if (eligibilityData.notEligible.length === eligibilityData.total) {
        addToast({
          type: 'error',
          heading: 'Error requesting grades',
          message: 'None of the selected submissions are eligible for grading.',
          life: 5000,
        })
        return
      }

      let regrade: null | boolean = null
      let excludeEdited = false

      // 2.2 If some are regrade eligible, show a dialog to ask if they want to regrade
      if (eligibilityData.edited?.length > 0) {
        removeToastsByType('spinner')
        const choice = await askRegradeWithEdits(
          eligibilityData.regradeEligible.length === eligibilityData.total,
          eligibilityData.edited.length,
        )

        if (choice === null) return

        regrade = choice.regrade
        excludeEdited = choice.excludeEdited

        addToast({
          type: 'spinner',
          heading: 'Requesting grades',
          message: 'Requesting grades...',
        })
      } else if (eligibilityData.regradeEligible.length > 0) {
        const message =
          eligibilityData.regradeEligible.length === eligibilityData.total
            ? 'All selected submissions have already been graded. Would you like to regrade these submissions?'
            : 'Some of the selected submissions have already been graded. Would you like to regrade these submissions?'

        // Suspend the spinner while the user makes a decision, then restore it.
        removeToastsByType('spinner')
        regrade = await showDialog<boolean>({
          type: 'confirm',
          props: {
            heading: 'Regrade submissions',
            message,
            confirmButtonLabel: 'Request regrade',
            cancelButtonLabel: 'Ignore Graded Submissions',
          },
        })

        if (regrade === null) return

        addToast({
          type: 'spinner',
          heading: 'Requesting grades',
          message: 'Requesting grades...',
        })
      } else {
        regrade = false
      }

      // If you aren't regrading and there are no eligible submissions (ie all selected are either not eligible or already graded), toast and return
      if (!regrade && eligibilityData.eligible.length === 0) {
        addToast({
          type: 'default',
          heading: 'Grade Aborted',
          message:
            'The grade request was aborted because all eligible selected submissions have already been graded.',
          life: 5000,
        })
        return
      }

      if (
        regrade &&
        excludeEdited &&
        eligibilityData.eligible.length +
          eligibilityData.regradeEligible.length -
          eligibilityData.edited.length ===
          0
      ) {
        addToast({
          type: 'default',
          heading: 'Grade Aborted',
          message:
            'The grade request was aborted because every selected graded submission was edited by a lecturer.',
          life: 5000,
        })
        return
      }

      // 3. REQUEST THE GRADES
      // Using the selection state
      // - This will ignore incomplete submissions and, if regrade is false, it will ignore graded submissions
      const { data, error } =
        await testSubmissionGradingService.batchRequestTestAttemptGrades(
          testId.value,
          withActiveFilters({ ...request, regrade, excludeEdited }),
        )

      deselectAll()

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error requesting grades',
          message: 'There was an issue requesting the grades.',
          life: 5000,
        })
        return
      }

      const ignoredGraded = regrade
        ? null
        : eligibilityData.regradeEligible.length
      const ignoredEdited =
        regrade && excludeEdited ? eligibilityData.edited.length : 0
      const ignoredIncomplete = regrade
        ? data.ignoredCount - ignoredEdited
        : data.ignoredCount - (ignoredGraded ?? 0)

      // If all were successful, show a success toast
      if (data.eligibleCount === data.successfulCount) {
        addToast({
          type: 'default',
          heading: 'Grades requested',
          message: data.ignoredCount
            ? `Grades requested successfully. ${data.ignoredCount} submission${data.ignoredCount === 1 ? ' was' : 's'} ignored${ignoredGraded ? ` (${ignoredGraded} already graded)` : ''}${ignoredEdited ? ` (${ignoredEdited} edited by a lecturer)` : ''}${ignoredIncomplete ? ` (${ignoredIncomplete} incomplete)` : ''}`
            : 'Grades requested successfully.',
          life: 5000,
        })
      } else if (data.eligibleCount === data.failedCount) {
        // If all were failed, show an error toast
        addToast({
          type: 'error',
          heading: 'All grades failed to request',
          message: 'All grades failed to request. Please try again.',
          life: 5000,
        })
      } else {
        // If some were successful and some were failed, show an error toast
        addToast({
          type: 'error',
          heading: 'Some grades were not requested',
          message: data.ignoredCount
            ? `${data.successfulCount} grade${data.successfulCount === 1 ? ' was' : 's were'} requested successfully. ${data.failedCount} grade${data.failedCount === 1 ? '' : 's'} failed to request. ${data.ignoredCount} submission${data.ignoredCount === 1 ? ' was' : 's'} ignored${ignoredGraded ? ` (${ignoredGraded} already graded)` : ''}${ignoredEdited ? ` (${ignoredEdited} edited by a lecturer)` : ''}${ignoredIncomplete ? ` (${ignoredIncomplete} incomplete)` : ''}`
            : `${data.successfulCount} grade${data.successfulCount === 1 ? ' was' : 's were'} requested successfully. ${data.failedCount} grade${data.failedCount === 1 ? '' : 's'} failed to request.`,
          life: 5000,
        })
      }
    } finally {
      isRequestingGrades.value = false
      removeToastsByType('spinner')
    }
  }

  // GROUPS ===============================================
  async function assignSubmissionToGroup(
    testAttemptId: string,
    groupId: string,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const { error } =
      await testSubmissionGroupsService.assignSubmissionsToGroup(
        testId.value,
        groupId,
        { ids: [testAttemptId], isAllSelected: false },
      )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error assigning submission to group',
        message: 'There was an issue assigning the submission to the group.',
        life: 5000,
      })
      return
    }

    addToast({
      type: 'default',
      heading: 'Submission assigned to group',
      message: 'Submission assigned to group successfully.',
      life: 5000,
    })

    await Promise.all([$refresh(), testSubmissionGroupsStore.$refresh()])
  }

  async function removeSubmissionFromGroup(
    testAttemptId: string,
    groupId: string,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const { error } =
      await testSubmissionGroupsService.removeSubmissionsFromGroup(
        testId.value,
        groupId,
        [testAttemptId],
      )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error removing submission from group',
        message: 'There was an issue removing the submission from the group.',
        life: 5000,
      })
      return
    }

    addToast({
      type: 'default',
      heading: 'Submission removed from group',
      message: 'Submission removed from group successfully.',
      life: 5000,
    })

    await Promise.all([$refresh(), testSubmissionGroupsStore.$refresh()])
  }

  async function batchAssignSubmissionsToGroup(
    groupId: string,
    selection: TestAttemptBatchSelectionParameters,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const { error } =
      await testSubmissionGroupsService.assignSubmissionsToGroup(
        testId.value,
        groupId,
        withActiveFilters(selection),
      )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error assigning submissions to group',
        message: 'There was an issue assigning the submissions to the group.',
        life: 5000,
      })
      return
    }

    addToast({
      type: 'default',
      heading: 'Submissions assigned to group',
      message: 'Submissions assigned to group successfully.',
      life: 5000,
    })

    deselectAll()

    await Promise.all([$refresh(), testSubmissionGroupsStore.$refresh()])
  }

  function getSubmissionsByGroupId(groupId: string) {
    return submissionsPaginated.value.filter((submission) =>
      submission.groupIds.includes(groupId),
    )
  }

  // DELETE SUBMISSIONS =========================================
  async function deleteSubmission(testAttemptId: string) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    const { error } = await testService.deleteTestAttempt(
      testId.value,
      testAttemptId,
    )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error deleting submission',
        message: 'There was an issue deleting the submission.',
        life: 5000,
      })
      return
    }

    addToast({
      type: 'default',
      heading: 'Submission deleted',
      message: 'Submission deleted successfully.',
      life: 5000,
    })

    await $refresh()
  }

  async function batchDeleteSubmissions(
    selection: TestAttemptBatchSelectionParameters,
  ) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    addToast({
      type: 'spinner',
      heading: 'Deleting submissions',
      message: 'Deleting submissions...',
    })

    const { data, error } = await testService.batchDeleteTestAttempts(
      testId.value,
      withActiveFilters(selection),
    )

    removeToastsByType('spinner')

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error deleting submissions',
        message:
          'The submissions could not be deleted because there was a server error. Please try again.',
        life: 5000,
      })
      await $refresh()
      return
    }

    if (data.failedCount === data.totalCount) {
      addToast({
        type: 'error',
        heading: 'Error deleting submissions',
        message:
          'The submissions could not be deleted because there was a server error. Please try again.',
        life: 5000,
      })
    } else if (data.failedCount > 0) {
      addToast({
        type: 'error',
        heading: 'Error deleting submissions',
        message: `${data.successfulCount} submission${data.successfulCount === 1 ? '' : 's'} deleted successfully, but ${data.failedCount} submission${data.failedCount === 1 ? '' : 's'} failed to delete.`,
        life: 5000,
      })
    } else {
      addToast({
        type: 'default',
        heading: 'Submissions deleted',
        message: `${data.successfulCount} submission${data.successfulCount === 1 ? '' : 's'} deleted successfully.`,
        life: 5000,
      })
    }

    deselectAll()

    await $refresh()
  }

  // DOWNLOAD CSV =========================================
  async function downloadAsCsv(withRemarks: boolean) {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    addToast({
      type: 'spinner',
      heading: 'Preparing CSV',
      message:
        'The CSV file is being prepared. Your download will start shortly.',
    })

    const { data: downloadLink, error } = await testService.getResultsCsvLink(
      testId.value,
      withRemarks,
    )

    if (error !== null) {
      removeToastsByType('spinner')
      addToast({
        type: 'error',
        heading: 'Error downloading CSV',
        message: 'There was an issue downloading the CSV.',
        life: 5000,
      })
      return
    }

    downloadFile(document, downloadLink)

    removeToastsByType('spinner')
  }

  async function downloadGradingVariabilityResultsCsv() {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    addToast({
      type: 'spinner',
      heading: 'Preparing CSV',
      message:
        'The CSV file is being prepared. Your download will start shortly.',
    })

    const { data: downloadLink, error } =
      await testService.getGradingVariabilityResultsCsvLink(testId.value)

    if (error) {
      removeToastsByType('spinner')
      addToast({
        type: 'error',
        heading: 'Error downloading CSV',
        message: 'There was an issue downloading the CSV.',
        life: 5000,
      })
      return
    }

    downloadFile(document, downloadLink)

    removeToastsByType('spinner')
  }

  // FILE SUBMISSIONS =========================================
  async function handleFileSubmission(files: File[] | FileList) {
    await testSubmissionGroupsStore.$ensureInitialised()

    let selectedGroupIds: string[] = []

    const groupOptions = testSubmissionGroupsStore.groups.filter(
      (g) => g.status === SubmissionGroupStatus.OPEN,
    )

    if (groupOptions.length > 0) {
      const dialogResult = await showDialog<string[]>({
        type: 'file-submission-upload',
        props: {
          submissionGroups: groupOptions,
          fileCount: files.length,
        },
      })

      if (!dialogResult) {
        return
      }

      selectedGroupIds = dialogResult
    }

    const batchId = crypto.randomUUID()

    uploadBatches.value[batchId] = {
      uploadedCount: 0,
      totalCount: Array.from(files).length,
      canClose: false,
    }

    addToast({
      type: 'file-submission-upload',
      heading: 'Uploading',
      message: 'Uploading submission(s) to the server.',
      closeable: false,
      data: { batchId },
    })

    await workspaceStore.$ensureInitialised()

    if (!workspaceStore.currentWorkspace) {
      clearUploadProgressToast(batchId)
      addToast({
        type: 'error',
        heading: 'Error uploading submissions',
        message: 'No workspace selected.',
        life: 5000,
      })
      return
    }

    const { error } = await testService.uploadFileSubmissions(
      testId.value as string,
      files,
      `ws:${workspaceStore.currentWorkspace.id}`,
      selectedGroupIds,
      {
        batchId,
        onProgress: (uploadedCount, totalCount) => {
          if (!uploadBatches.value[batchId]) {
            return
          }

          uploadBatches.value[batchId].uploadedCount = uploadedCount
          uploadBatches.value[batchId].totalCount = totalCount
        },
      },
    )

    if (error !== null) {
      clearUploadProgressToast(batchId)
      addToast({
        type: 'error',
        heading: 'Error uploading submissions',
        message:
          'There was an issue uploading the submissions. Please try again.',
        life: 5000,
      })
      return
    }

    // HTTP request succeeded — allow manual dismissal so the user always has
    // an escape hatch if SSE is slow or never arrives.
    if (uploadBatches.value[batchId]) {
      uploadBatches.value[batchId].canClose = true
    }

    // Start the inactivity watchdog. The timer resets on every SSE update, so
    // the toast only auto-closes after 2 minutes with no SSE activity — it
    // won't fire prematurely while a long upload is still making progress.
    scheduleUploadFallbackClear(batchId)

    await $refresh()
  }

  // GOOGLE DRIVE SUBMISSIONS =========================================
  async function handleGoogleDriveSubmission() {
    if (!workspaceStore.currentWorkspace) {
      return
    }

    const token = await authenticate()
    if (!token) return

    const docs = await openPicker(token)
    if (!docs) return

    const items = docs.map((doc) => ({
      id: doc.id,
      name: doc.name,
      mimeType: doc.mimeType,
      sizeBytes: doc.sizeBytes,
      isFolder: doc.type === 'folder',
    }))

    addToast({
      type: 'spinner',
      heading: 'Google Drive',
      message: 'Requesting files from Google Drive...',
    })

    const { data, error } =
      await googleDriveSubmissionsService.postGoogleDriveSubmissions(
        testId.value as string,
        token,
        items,
        `ws:${workspaceStore.currentWorkspace.id}`,
      )

    removeToastsByType('spinner')

    if (error || !data) {
      addToast({
        type: 'error',
        heading: 'Google Drive',
        message:
          'There was an issue uploading from Google Drive. Please try again.',
        life: 5000,
      })
      return
    }

    const { batchId, eligibleFiles, skippedFiles } = data

    uploadBatches.value[batchId] = {
      uploadedCount: 0,
      totalCount: eligibleFiles,
      canClose: true,
      ...(skippedFiles > 0 && { skippedFiles }),
    }

    addToast({
      type: 'file-submission-upload',
      heading: 'Uploading',
      message: 'Uploading submission(s) from Google Drive.',
      closeable: false,
      data: { batchId },
    })

    scheduleUploadFallbackClear(batchId)
  }

  // LTI GRADE SYNC =========================================
  const testHasDeepLinks = ref(false)

  async function syncGrades() {
    if (!testId.value || typeof testId.value !== 'string') {
      return
    }

    addToast({
      type: 'spinner',
      heading: 'Syncing grades',
      message: 'Syncing grades with integrated LTI platforms...',
    })

    const { data, error } = await ltiGradeSyncService.postSyncGradesByTestId(
      testId.value,
    )

    removeToastsByType('spinner')

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error syncing grades',
        message: 'There was an issue syncing the grades.',
        life: 5000,
      })
      return
    }

    console.info('Grade sync request successful: ', data)

    addToast({
      type: 'default',
      heading: 'Grades sync requested',
      message: `Successful: ${data.synced}, Failed: ${data.failed}, Skipped: ${data.skipped}, Total: ${data.total}`,
    })
  }

  async function handleFileSubmissionBatchUpdate(
    data: FileSubmissionBatchUpdateMessage,
  ) {
    const batchId = data.id

    if (!uploadBatches.value[batchId]) return

    // Mark as done and store the authoritative counts.
    uploadBatches.value[batchId].batchDone = true
    uploadBatches.value[batchId].uploadedCount = data.data.successfulCount
    uploadBatches.value[batchId].totalCount = data.data.totalCount

    if (data.data.failedCount > 0) {
      uploadBatches.value[batchId].failedCount = data.data.failedCount
    }

    if (data.data.skippedCount > 0) {
      uploadBatches.value[batchId].skippedFiles = data.data.skippedCount
    }

    await refreshSubmissionsPaginated()
  }

  // SSE EVENT SUBSCRIPTIONS ====================================
  onEvent(
    SseMessageType.TEST_ATTEMPT_GRADE_STATUS_UPDATE,
    handleTestAttemptGradeStatusUpdate,
  )
  onEvent(
    SseMessageType.TEST_GRADE_RANKING_INVALIDATION_UPDATE,
    handleTestGradeRankingInvalidationUpdate,
  )
  onEvent(
    SseMessageType.TEST_GRADE_RANKING_UPDATE,
    handleTestGradeRankingUpdate,
  )
  onEvent(
    SseMessageType.TEST_ATTEMPT_STATUS_UPDATE,
    handleTestAttemptStatusUpdate,
  )
  onEvent(
    SseMessageType.FILE_SUBMISSION_BATCH_UPDATE,
    handleFileSubmissionBatchUpdate,
  )

  // STORE MANAGER =======================================
  function reset() {
    testGradeRanking.value = null
    listCounts.value = null
    listUpdateRequestId++
    // Filters are plain refs on this singleton store, so clear them per test to
    // stop one test's filters leaking into another's list and batch payloads.
    searchQuery.value = ''
    fileSubmissionStatus.value = ''
    gradingStatus.value = ''
    groupIds.value = ''
    flags.value = ''
    gradeMin.value = ''
    gradeMax.value = ''
  }

  const {
    $isInitialised,
    $isInitialising,
    $initialise,
    $ensureInitialised,
    $reset,
  } = useStoreManager(
    computed(() => testId.value),

    async () => {
      reset()

      if (!testId.value || typeof testId.value !== 'string') {
        return false
      }

      await workspaceStore.$ensureInitialised()

      await populateTestGradeRanking(testId.value)

      if (workspaceStore.workspaceHasActiveLtiPlatforms) {
        const { data, error } =
          await ltiGradeSyncService.getSyncGradesStatusByTestId(testId.value)

        if (error !== null) {
          console.warn(
            'Unable to fetch grade sync status for test: ',
            testId.value,
          )
          return true
        }

        testHasDeepLinks.value = data.hasDeepLinks
      }

      return true
    },

    reset,
  )

  async function $refresh() {
    await refreshSubmissionsPaginated()
  }

  return {
    uploadBatches,
    clearUploadProgressToast,
    $isInitialised,
    $isInitialising,
    $initialise,
    $ensureInitialised,
    $reset,
    $refresh,
    downloadAsCsv,
    downloadGradingVariabilityResultsCsv,
    handleFileSubmission,
    handleGoogleDriveSubmission,
    requestTestAttemptGrade,
    requestTestAttemptReGrade,
    retryFileSubmissionConversion,
    countEligibleSubmissionsForReprocessing,
    batchRetryFileSubmissionConversion,
    batchRequestTestAttemptGrades,
    isRequestingGrades,
    regradePromptDetails,
    isRegradePromptOpen,
    answerRegradePrompt,
    dismissRegradePrompt,
    assignSubmissionToGroup,
    removeSubmissionFromGroup,
    batchAssignSubmissionsToGroup,
    deleteSubmission,
    batchDeleteSubmissions,
    getSubmissionsByGroupId,
    testGradeRanking,
    forceRefreshTestGradeRanking,
    testId,

    // PAGINATED
    submissionsPaginated,
    isLoading,
    totalItems,
    page,
    rowsPerPage,
    sortField,
    sortOrder,
    paginationError,
    searchQuery,
    fileSubmissionStatus,
    gradingStatus,
    groupIds,
    flags,
    gradeMin,
    gradeMax,
    flagCounts,
    flaggedCount,
    unfilteredTotalItems,

    // TABLE SELECTION
    selectionState,
    selectedCount,
    toggleSelectedId,
    selectRange,
    selectAll,
    deselectAll,

    // LTI GRADE SYNC
    testHasDeepLinks,
    syncGrades,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useTestSubmissionsStore, import.meta.hot),
  )
}
